import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode, Suspense } from "react";

const CHUNK_LOAD_ERROR_MESSAGES = [
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",
];

function isChunkLoadError(error: Error): boolean {
    const message = error instanceof Error ? error.message.toLowerCase() : "";

    return CHUNK_LOAD_ERROR_MESSAGES.some((candidate) =>
        message.includes(candidate),
    );
}

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
}

export class LazyChunkBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (isChunkLoadError(error)) {
            return;
        }

        Sentry.captureException(error, (scope) =>
            scope.setExtra("componentStack", errorInfo.componentStack),
        );
    }

    override render() {
        const { hasError } = this.state;
        const { children, fallback = null } = this.props;

        if (hasError) {
            return fallback;
        }

        return <Suspense fallback={fallback}>{children}</Suspense>;
    }
}
